import React, { useMemo } from 'react'
import { useI18n } from '../../i18n'
import { ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts'
import { FolderOpen } from 'lucide-react'
import { SecurityCard } from '../ui/SecurityCard'
import type { CurrentVulnerabilityPosture, VulnerabilityTrendPoint } from '../../services/vulnerabilityPostureApi'
import { countAssetsWithSeverity } from '../../services/vulnerabilityPostureApi'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import styles from '../../styles/components/assets/AssetsInsights.module.css'

interface Asset {
  id: string
  name: string
  criticality?: string
  vulnerabilities: {
    critical: number
    high: number
    medium: number
    low: number
    total: number
  }
  lastScan?: string
}

interface VulnerabilityExposureCardProps {
  assets: Asset[]
  posture?: CurrentVulnerabilityPosture | null
  trend?: VulnerabilityTrendPoint[]
  postureLoading?: boolean
  scanHistory?: Array<{
    date: string
    critical: number
    high: number
    medium: number
    low: number
    fixed: number
  }>
}

function summaryColor(pct: number, empty: boolean): string {
  if (empty) return '#9ca3af'
  if (pct >= 70) return '#16a34a'
  if (pct >= 40) return '#ca8a04'
  return '#dc2626'
}

const VulnerabilityExposureCard: React.FC<VulnerabilityExposureCardProps> = ({
  assets,
  posture = null,
  trend = [],
  postureLoading = false,
  scanHistory = [],
}) => {
  const { t } = useI18n()

  const usePosture = Boolean(posture)

  const exposureSummary = useMemo(() => {
    if (usePosture && posture) {
      const totalAssets = posture.assetsTotal || assets.length
      const assetsWithVulns = posture.assetsWithVulnerabilities
      const cleanAssetsPct =
        totalAssets > 0 ? Math.round(((totalAssets - assetsWithVulns) / totalAssets) * 100) : 0
      const lowerSeveritySharePct =
        posture.total > 0
          ? Math.round(((posture.medium + posture.low + posture.info) / posture.total) * 100)
          : 100
      return {
        empty: totalAssets === 0 && posture.total === 0,
        cleanAssetsPct,
        lowerSeveritySharePct,
        totalVulns: posture.total,
        totalAssets,
        assetsWithVulns,
        critical: posture.critical,
        high: posture.high,
        assetsWithCritical: countAssetsWithSeverity(posture.byAssetName, 'critical'),
        assetsWithHigh: countAssetsWithSeverity(posture.byAssetName, 'high'),
      }
    }

    const n = assets.length
    if (n === 0) {
      return {
        empty: true,
        cleanAssetsPct: 0,
        lowerSeveritySharePct: 0,
        totalVulns: 0,
        totalAssets: 0,
        assetsWithVulns: 0,
        critical: 0,
        high: 0,
        assetsWithCritical: 0,
        assetsWithHigh: 0,
      }
    }
    const totalVulns = assets.reduce((sum, a) => sum + (a.vulnerabilities?.total ?? 0), 0)
    const crit = assets.reduce((sum, a) => sum + a.vulnerabilities.critical, 0)
    const high = assets.reduce((sum, a) => sum + a.vulnerabilities.high, 0)
    const assetsWithVulns = assets.filter((a) => (a.vulnerabilities?.total ?? 0) > 0).length
    const cleanAssetsPct = Math.round(((n - assetsWithVulns) / n) * 100)
    const lowerSeveritySharePct =
      totalVulns > 0 ? Math.round(((totalVulns - crit - high) / totalVulns) * 100) : 100
    return {
      empty: false,
      cleanAssetsPct,
      lowerSeveritySharePct,
      totalVulns,
      totalAssets: n,
      assetsWithVulns,
      critical: crit,
      high,
      assetsWithCritical: assets.filter((a) => a.vulnerabilities.critical > 0).length,
      assetsWithHigh: assets.filter((a) => a.vulnerabilities.high > 0).length,
    }
  }, [assets, posture, usePosture])

  const topVulnerableAssets = useMemo(() => {
    if (usePosture && posture?.topAssets?.length) {
      return posture.topAssets.slice(0, 10).map((a) => ({
        name: a.name.length > 15 ? `${a.name.substring(0, 15)}...` : a.name,
        fullName: a.name,
        total: a.vulnerabilityCount,
      }))
    }
    return [...assets]
      .sort((a, b) => (b.vulnerabilities?.total ?? 0) - (a.vulnerabilities?.total ?? 0))
      .slice(0, 10)
      .map((a) => ({
        name: a.name.length > 15 ? `${a.name.substring(0, 15)}...` : a.name,
        fullName: a.name,
        total: a.vulnerabilities?.total ?? 0,
      }))
  }, [assets, posture, usePosture])

  const severityDistribution = useMemo(() => {
    let critical: number
    let high: number
    let medium: number
    let low: number

    if (usePosture && posture) {
      critical = posture.critical
      high = posture.high
      medium = posture.medium
      low = posture.low
    } else {
      critical = assets.reduce((sum, a) => sum + a.vulnerabilities.critical, 0)
      high = assets.reduce((sum, a) => sum + a.vulnerabilities.high, 0)
      medium = assets.reduce((sum, a) => sum + a.vulnerabilities.medium, 0)
      low = assets.reduce((sum, a) => sum + a.vulnerabilities.low, 0)
    }

    const total = critical + high + medium + low
    if (total === 0) {
      return [{ name: 'No Data', value: 1, color: '#9ca3af' }]
    }

    return [
      { name: 'Critical', value: critical, color: '#8B0000' },
      { name: 'High', value: high, color: '#DC2626' },
      { name: 'Medium', value: medium, color: '#56a3d9' },
      { name: 'Low', value: low, color: '#10B981' },
    ].filter((item) => item.value > 0)
  }, [assets, posture, usePosture])

  const trendData = useMemo(() => {
    if (trend.length > 0) {
      return trend.map((point) => ({
        month: point.date,
        detected: point.detected,
        fixed: point.fixed ?? 0,
      }))
    }
    if (scanHistory.length > 0) {
      return scanHistory.map((scan) => ({
        month: new Date(scan.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        detected: scan.critical + scan.high + scan.medium + scan.low,
        fixed: scan.fixed || 0,
      }))
    }
    return []
  }, [trend, scanHistory])

  const hasFixedTrend = trendData.some((item) => (item.fixed || 0) > 0)

  const heatmapData = useMemo(() => {
    const criticalities = ['Critical', 'High', 'Medium', 'Low', 'Unknown']
    const severities = ['Critical', 'High', 'Medium', 'Low']

    return criticalities.map((crit) => {
      const assetsWithCrit = assets.filter((a) => (a.criticality || 'Unknown') === crit)
      const data: Record<string, string | number> = { criticality: crit }

      severities.forEach((sev) => {
        const sevKey = sev.toLowerCase() as 'critical' | 'high' | 'medium' | 'low'
        data[sev] = assetsWithCrit.reduce((sum, a) => sum + a.vulnerabilities[sevKey], 0)
      })

      return data
    })
  }, [assets])

  const getHeatmapIntensity = (value: number) => {
    if (value === 0) return 'bg-gray-100 dark:bg-gray-800'
    if (value <= 2) return 'bg-yellow-200 dark:bg-yellow-900'
    if (value <= 5) return 'bg-orange-300 dark:bg-orange-800'
    return 'bg-red-400 dark:bg-red-900'
  }

  const { empty: noAssets, cleanAssetsPct, lowerSeveritySharePct } = exposureSummary

  return (
    <div className={`${styles.sectionCard} ${styles.stackGap}`}>
      <h2 className={styles.sectionHeader}>
        <FolderOpen className={styles.sectionIcon} aria-hidden />
        {t('assets.vulnerabilityExposure.overview', 'Vulnerability Exposure')}
        {postureLoading ? (
          <span className={styles.emptyHint} style={{ marginLeft: '0.5rem', fontWeight: 400 }}>
            {t('common.loading', 'Loading…')}
          </span>
        ) : null}
      </h2>

      {usePosture ? (
        <div className={entStyles.postureGrid}>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>{t('assets.overview.totalAssets', 'Total assets')}</p>
            <p className={entStyles.postureMetricValue}>{exposureSummary.totalAssets}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>
              {t('reports.metrics.assetsWithVulns', 'Assets with vulnerabilities')}
            </p>
            <p className={entStyles.postureMetricValue}>{exposureSummary.assetsWithVulns}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>{t('labels.severityCritical', 'Critical')}</p>
            <p className={entStyles.postureMetricValue}>{exposureSummary.critical}</p>
            <p className={entStyles.postureSubtitle}>
              {t('enterpriseOperationalUx.assetInventory.vulnAcrossAssets', '{{count}} assets with findings', {
                count: exposureSummary.assetsWithCritical,
              })}
            </p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>{t('labels.severityHigh', 'High')}</p>
            <p className={entStyles.postureMetricValue}>{exposureSummary.high}</p>
            <p className={entStyles.postureSubtitle}>
              {t('enterpriseOperationalUx.assetInventory.vulnAcrossAssets', '{{count}} assets with findings', {
                count: exposureSummary.assetsWithHigh,
              })}
            </p>
          </div>
        </div>
      ) : (
        <div className={styles.exposureSummaryRow}>
          <div className={styles.exposureCardSlot}>
            <SecurityCard
              title={t('assets.vulnerabilityExposure.cleanAssets', 'Assets with no findings')}
              percent={cleanAssetsPct}
              size="small"
              color={summaryColor(cleanAssetsPct, noAssets)}
              chartSize={72}
              innerRadius={24}
              outerRadius={32}
            />
          </div>
          <div className={styles.exposureCardSlot}>
            <SecurityCard
              title={t('assets.vulnerabilityExposure.lowerSeverityShare', 'Medium + low share of findings')}
              percent={lowerSeveritySharePct}
              size="small"
              color={summaryColor(lowerSeveritySharePct, noAssets)}
              chartSize={72}
              innerRadius={24}
              outerRadius={32}
            />
          </div>
        </div>
      )}

      <div>
        <h3 className={styles.subsectionTitle}>
          {t('assets.vulnerabilityExposure.topVulnerable', 'Top-10 Vulnerable Assets')}
        </h3>
        {topVulnerableAssets.length === 0 ? (
          <p className={styles.emptyHint}>{t('common.noData', 'No data available')}</p>
        ) : (
          <div className={styles.chartPlot}>
            <div style={{ width: '100%', height: 128 }}>
              <ResponsiveContainer width="100%" height={128} minHeight={128}>
                <BarChart data={topVulnerableAssets} layout="vertical" margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 9 }} />
                  <Tooltip />
                  <Bar dataKey="total" fill="#ef4444" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      <div>
        <h3 className={styles.subsectionTitle}>
          {t('assets.vulnerabilityExposure.severityDistribution', 'Vulnerability Distribution by Severity')}
        </h3>
        {severityDistribution.length === 0 ||
        severityDistribution.every((item) => item.value === 0) ||
        (severityDistribution.length === 1 && severityDistribution[0].name === 'No Data') ? (
          <p className={styles.emptyHint}>{t('common.noData', 'No data available')}</p>
        ) : (
          <div className={styles.chartPlot}>
            <div style={{ width: '100%', height: 140 }}>
              <ResponsiveContainer width="100%" height={140} minHeight={140}>
                <PieChart>
                  <Pie
                    data={severityDistribution}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={50}
                    startAngle={90}
                    endAngle={-270}
                  >
                    {severityDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: '10px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      <div>
        <h3 className={styles.subsectionTitle}>
          {t('assets.vulnerabilityExposure.trend', 'Vulnerability Trend Over Time')}
        </h3>
        {trendData.length === 0 ? (
          <p className={styles.emptyHint}>{t('common.noData', 'No data available')}</p>
        ) : (
          <div className={styles.chartPlotTall}>
            <div style={{ width: '100%', height: 160 }}>
              <ResponsiveContainer width="100%" height={160} minHeight={160}>
                <LineChart data={trendData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 9 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: '10px' }} />
                  <Line type="monotone" dataKey="detected" stroke="#ef4444" strokeWidth={2} name="Detected" />
                  {hasFixedTrend ? (
                    <Line type="monotone" dataKey="fixed" stroke="#10b981" strokeWidth={2} name="Fixed" />
                  ) : null}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      <div>
        <h3 className={styles.subsectionTitle}>
          {t('assets.vulnerabilityExposure.heatmap', 'Severity × Asset Criticality')}
        </h3>
        {heatmapData.length === 0 ||
        heatmapData.every(
          (row) =>
            (row.Critical || 0) === 0 &&
            (row.High || 0) === 0 &&
            (row.Medium || 0) === 0 &&
            (row.Low || 0) === 0,
        ) ? (
          <p className={styles.emptyHint}>{t('common.noData', 'No data available')}</p>
        ) : (
          <div className={styles.miniTableWrap}>
            <table className={styles.miniTable}>
              <thead>
                <tr>
                  <th>{t('assets.vulnerabilityExposure.criticality', 'Criticality')}</th>
                  <th className="text-center">Critical</th>
                  <th className="text-center">High</th>
                  <th className="text-center">Medium</th>
                  <th className="text-center">Low</th>
                </tr>
              </thead>
              <tbody>
                {heatmapData.map((row, idx) => (
                  <tr key={idx}>
                    <td className="font-medium">{row.criticality}</td>
                    <td className={`text-center ${getHeatmapIntensity(Number(row.Critical || 0))}`}>
                      {row.Critical || 0}
                    </td>
                    <td className={`text-center ${getHeatmapIntensity(Number(row.High || 0))}`}>
                      {row.High || 0}
                    </td>
                    <td className={`text-center ${getHeatmapIntensity(Number(row.Medium || 0))}`}>
                      {row.Medium || 0}
                    </td>
                    <td className={`text-center ${getHeatmapIntensity(Number(row.Low || 0))}`}>
                      {row.Low || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default VulnerabilityExposureCard
